import axios from 'axios';
import { getBestTier } from '@/Util/helpers.js';
import { EMAIL_REGEX, PHONE_REGEX } from '@/Util/constants.js';
import { PlanNotFound, AttachmentNotFound } from '@/Util/errors.js';

export default async (state, commit, getters) => {

    try {
        if (getters.isCuratedLink) {
            commit('setCurrentStep', 1);
        }
        // Set plan Id based on edition
        if (!!getters.purchaseInfo.planEdition) {
            let plan = getters.allPlans.find(plan => plan.aliases.includes(getters.purchaseInfo.planEdition));
            commit('updateCartInfo', {selectedPlanId: plan.id});
        }
        // End set plan Id based on edition

        if  (!getters.selectedPlan) {
            throw new PlanNotFound('Selected plan not found', {selectedPlanId: getters.cartInfo.selectedPlanId});
        }

        // Handle additional contacts
        let includedContacts = getters.includedAddons.find(addon => addon.itemId.toLowerCase().includes('contact'));
        let additionalContacts = getters.purchaseInfo.contacts - includedContacts.quantity;
        if (additionalContacts < 0) {
            additionalContacts = 0;
        }
        if (additionalContacts > 0) {
            let item = getters.optionalAddons.find(addon => {
                return addon.aliases.includes('contacts');
            });
            if (!!item) {
                let bestTier = getBestTier(item.pricing.find(price => price.billingPeriodUnit === getters.billingPeriod).tiers, additionalContacts);
                let quantity = bestTier.maxUnits;
                if (!quantity) quantity = bestTier.minUnits;
                if (!getters.cartInfo.selectedAddons.find(i => i.itemId === item.itemId)) {
                    commit('addAttachment', { itemId: item.itemId, quantity: quantity});
                }
            }
        }
        // End handle additional contacts
        // Handle additional users
        let includedUsers = getters.includedAddons.find(addon => addon.itemId.toLowerCase().includes('user'));
        let additionalUsers = getters.purchaseInfo.users - includedUsers.quantity;
        if (additionalUsers < 0) {
            additionalUsers = 0;
        }
        if (additionalUsers > 0) {
            let item = getters.optionalAddons.find(addon => {
                return addon.aliases.includes('users');
            });
            if (!!item && !getters.cartInfo.selectedAddons.find(i => i.itemId === item.itemId)) {
                commit('addAttachment', { itemId: item.itemId, quantity: additionalUsers});
            }
        }
        // End handle additional users
        // Handle kickstart IDs
        if (!!getters.purchaseInfo.kickstartId) {
            let foundKickstart = getters.optionalCharges.find(item => {
                return item.itemId === getters.purchaseInfo.kickstartId || item.aliases.includes(getters.purchaseInfo.kickstartId);
            });
            if (!!foundKickstart) {
                commit('addAttachment', { itemId: foundKickstart.itemId, quantity: 1});
            }
        }
        // End handle kickstart IDs

        // Handle 500 additional messages
        let additionalMessages = getters.purchaseInfo.additionalMessages;
        if (!!additionalMessages && additionalMessages > 0) {
            let item = getters.optionalAddons.find(addon => {
                return addon.itemId.toLowerCase().includes('500-additional-message');
            });
            if (!getters.cartInfo.selectedAddons.find(i => i.itemId === item.itemId)) {
                commit('addAttachment', { itemId: item.itemId, quantity: additionalMessages});
            }
        }
        // End handle  500 additional messages
        // Handle text & voice
        let textVoiceTier = getters.purchaseInfo.textVoiceTier;
        let addons = [...getters.includedAddons, ...getters.optionalAddons];
        let item = addons.find(addon => {
            return addon.itemId.toLowerCase().includes('text-voice');
        });
        if (!!textVoiceTier && textVoiceTier > 0) {
            if (!getters.cartInfo.selectedAddons.find(i => i.itemId === item.itemId)) {
                commit('addAttachment', { itemId: item.itemId, quantity: textVoiceTier});
            }
        } else if (!textVoiceTier && textVoiceTier != 0) {
            if (!getters.cartInfo.selectedAddons.find(i => i.itemId === item.itemId)) {
                commit('addAttachment', { itemId: item.itemId, quantity: 1});
            }
        }
        // End handle text & voice

        // Store text & voice in temp storage if not in US or CA
        let mappedItems = getters.cartInfo.selectedAddons.map(item => {
            return {
                ...item,
                metadata: [...getters.selectedPlan.addons, ...getters.selectedPlan.charges].find(addon => addon.itemId === item.itemId)?.metadata
            }
        })
        let itemsToStore = mappedItems.filter(item => {
            if (!!item?.metadata?.showForCountries) {
                if (
                    getters.userInfo.currency != 'USD'
                    || !item.metadata.showForCountries.includes(getters.userInfo.country.toLowerCase())
                ) {
                    return true;
                }
            }
            return false;
        });
        if (!['us', 'ca'].includes(getters.userInfo.country.toLowerCase())) {
            itemsToStore.forEach(item => {
                commit('updateTempAttachmentStorage', {itemId: item.itemId, quantity: item.quantity});
                commit('removeAttachment', item.itemId);
            });
        }
        // End text & voice temp storage
        // Set valid state for each prefilled field
        for (const key in getters.userInfo) {
            if (getters.userInfo[key] !== '' && state.validation.hasOwnProperty(key)) {
                if (key === 'email') {
                    commit('updateValidation', {
                        [key]: {
                            valid: RegExp(EMAIL_REGEX).test(getters.userInfo[key])
                        }
                    });
                } else if (key === 'phone') {
                    commit('updateValidation', {
                        [key]: {
                            valid: RegExp(PHONE_REGEX).test(getters.userInfo[key])
                        }
                    });
                } else {
                    commit('updateValidation', {
                        [key]: {
                            valid: true
                        }
                    });
                }
            }
        }
        // End set valid state for each prefilled field

        // Make sure all attachments exist in the selected plan
        let attachments = [...getters.cartInfo.selectedAddons, ...getters.cartInfo.selectedCharges];
        let validAttachments = attachments.filter(attachment => {
            return !!getters.selectedPlan.addons.find(addon => addon.itemId === attachment.itemId) || !!getters.selectedPlan.charges.find(charge => charge.itemId === attachment.itemId);
        });
        if (attachments.length !== validAttachments.length) {
            const badAttachments = attachments.filter(attachment => {
                return !getters.selectedPlan.addons.find(addon => addon.itemId === attachment.itemId) && !getters.selectedPlan.charges.find(charge => charge.itemId === attachment.itemId);
            });
            throw new AttachmentNotFound(`One or more attachments are not valid for the selected plan`, {badAttachments: badAttachments});
        }
        // End make sure all attachments exist in the selected plan

        commit('setLoaded', true);
    } catch (error) {
        let eventId = '';
        if ((getters.cartInfo.pricing.plans.length > 0 && error instanceof PlanNotFound) || error instanceof AttachmentNotFound) {
            eventId = window.Sentry.logInfo(error, error.details || null);
        } else {
            eventId = window.Sentry.logError(error, error.details || null);
        }
        commit('setLinkError', {
            active: true,
            code: 1,
            eventId,
        });
    }
}
